import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Chip,
  Skeleton,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as CreateIcon,
  Delete as DeleteIcon,
  EditOutlined as EditModuleIcon,
  DeleteOutlined as DeleteModuleIcon,
  DoneAll as AllIcon,
} from '@mui/icons-material';

const PERMISSION_KEYS = [
  { key: 'CanView',   label: 'View',   Icon: ViewIcon,   color: '#2196f3' },
  { key: 'CanEdit',   label: 'Edit',   Icon: EditIcon,   color: '#ff9800' },
  { key: 'CanCreate', label: 'Create', Icon: CreateIcon, color: '#4caf50' },
  { key: 'CanDelete', label: 'Delete', Icon: DeleteIcon, color: '#f44336' },
];

/**
 * PermissionsMatrix
 * Props:
 *  - modules: Array<{ Id, Name, Category }> — from getAllModules()
 *  - permissions: Object  — { [ModuleName]: { CanView, CanEdit, CanCreate, CanDelete } }
 *  - onChange: (moduleName, permKey, value) => void
 *  - loading: boolean
 *  - readOnly: boolean
 */
const PermissionsMatrix = ({ modules = [], permissions = {}, onChange, loading = false, readOnly = false, onEditModule, onDeleteModule }) => {
  // Group modules by category and organize parent-child relationships
  const grouped = useMemo(() => {
    const map = new Map();
    
    // First, collect all parent modules (those without ParentModuleId)
    const parentModules = modules.filter(m => !m.ParentModuleId && !m.parentModuleId);
    
    parentModules.forEach((parent) => {
      const cat = parent.Category || parent.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      
      // Add parent module
      map.get(cat).push({ ...parent, isParent: true, level: 0 });
      
      // Add children from ChildModules array if it exists
      if (parent.ChildModules && Array.isArray(parent.ChildModules) && parent.ChildModules.length > 0) {
        parent.ChildModules.forEach(child => {
          map.get(cat).push({ ...child, isChild: true, level: 1, parentName: parent.ModuleName });
        });
      }
    });
    
    return map;
  }, [modules]);

  const showActions = !!(onEditModule || onDeleteModule);

  const handleCheck = useCallback((moduleName, permKey, checked) => {
    if (readOnly || !onChange) return;
    // CanView auto-enabled when any other perm is enabled
    if (permKey !== 'CanView' && checked && !permissions[moduleName]?.CanView) {
      onChange(moduleName, 'CanView', true);
    }
    // Disable all when CanView is unchecked
    if (permKey === 'CanView' && !checked) {
      ['CanEdit', 'CanCreate', 'CanDelete'].forEach((k) => onChange(moduleName, k, false));
    }
    onChange(moduleName, permKey, checked);
  }, [readOnly, onChange, permissions]);

  // Toggle all permissions for a module at once
  const handleSelectAll = useCallback((moduleName, checked) => {
    if (readOnly || !onChange) return;
    PERMISSION_KEYS.forEach(({ key }) => onChange(moduleName, key, checked));
  }, [readOnly, onChange]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[...Array(5)].map((_, i) => <Skeleton key={i} height={48} sx={{ mb: 0.5 }} />)}
      </Box>
    );
  }

  if (modules.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No modules available</Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', minWidth: 180, bgcolor: '#f5f5f5' }}>
              Module
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f5f5f5', width: 80 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                <AllIcon sx={{ fontSize: 15, color: '#667eea' }} />
                <span>All</span>
              </Box>
            </TableCell>
            {PERMISSION_KEYS.map(({ key, label, Icon, color }) => (
              <TableCell key={key} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f5f5f5', width: 80 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                  <Icon sx={{ fontSize: 15, color }} />
                  <span>{label}</span>
                </Box>
              </TableCell>
            ))}
            {showActions && (
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f5f5f5', width: 80 }}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...grouped.entries()].map(([category, mods]) => (
            <React.Fragment key={category}>
              {/* Category separator */}
              <TableRow>
                <TableCell
                  colSpan={showActions ? 7 : 6}
                  sx={{
                    bgcolor: '#ede7f6',
                    py: 0.5,
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#5e35b1',
                    borderBottom: '1px solid #d1c4e9',
                  }}
                >
                  {category}
                </TableCell>
              </TableRow>

              {mods.map((mod) => {
                const modName = mod.ModuleName || mod.Name || mod.name || mod.ModuleId;
                const modPerms = permissions[modName] || {};
                const hasAny = PERMISSION_KEYS.some(({ key }) => modPerms[key]);

                return (
                  <TableRow
                    key={`${mod.Id || modName}-${mod.level || 0}`}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      bgcolor: mod.isChild ? 'rgba(255,152,0,0.04)' : (hasAny ? 'rgba(103,126,234,0.04)' : 'white'),
                      borderLeft: mod.isChild ? '3px solid #ff9800' : 'none',
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.82rem', pl: mod.isChild ? 4 : 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {mod.isChild && (
                          <Chip
                            label="SUB"
                            size="small"
                            sx={{ 
                              height: 16, 
                              fontSize: '0.6rem', 
                              bgcolor: '#ff9800', 
                              color: 'white',
                              fontWeight: 700,
                              mr: 0.5,
                            }}
                          />
                        )}
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: mod.isParent ? 800 : (hasAny ? 700 : 400) }}>
                          {modName}
                        </Typography>
                        {hasAny && (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{ height: 16, fontSize: '0.62rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Select All Checkbox */}
                    <TableCell align="center" padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={
                          modPerms.CanView &&
                          modPerms.CanEdit &&
                          modPerms.CanCreate &&
                          modPerms.CanDelete
                        }
                        indeterminate={
                          (modPerms.CanView ||
                            modPerms.CanEdit ||
                            modPerms.CanCreate ||
                            modPerms.CanDelete) &&
                          !(
                            modPerms.CanView &&
                            modPerms.CanEdit &&
                            modPerms.CanCreate &&
                            modPerms.CanDelete
                          )
                        }
                        disabled={readOnly}
                        onChange={(e) => handleSelectAll(modName, e.target.checked)}
                        sx={{
                          color: '#667eea',
                          '&.Mui-checked': { color: '#667eea' },
                          '&.MuiCheckbox-indeterminate': { color: '#667eea' },
                          p: 0.5,
                        }}
                      />
                    </TableCell>

                    {PERMISSION_KEYS.map(({ key, color }) => {
                      const checked = !!modPerms[key];
                      const disabled = readOnly || (key !== 'CanView' && !modPerms.CanView);
                      return (
                        <TableCell key={key} align="center" padding="checkbox">
                          <Tooltip title={disabled && !readOnly ? 'Enable View first' : ''} placement="top">
                            <span>
                              <Checkbox
                                size="small"
                                checked={checked}
                                disabled={disabled}
                                onChange={(e) => handleCheck(modName, key, e.target.checked)}
                                sx={{
                                  color: checked ? color : 'action.disabled',
                                  '&.Mui-checked': { color },
                                  p: 0.5,
                                }}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                    {showActions && (
                      <TableCell align="center" padding="none" sx={{ whiteSpace: 'nowrap' }}>
                        {onEditModule && (
                          <Tooltip title="Edit module">
                            <IconButton size="small" onClick={() => onEditModule(mod)} sx={{ color: '#ff9800', p: 0.5 }}>
                              <EditModuleIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDeleteModule && (
                          <Tooltip title="Delete module">
                            <IconButton size="small" onClick={() => onDeleteModule(mod)} sx={{ color: '#f44336', p: 0.5 }}>
                              <DeleteModuleIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default React.memo(PermissionsMatrix);
